import { Directive, ElementRef, Injector, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NgControl, ValidationErrors, Validator } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

const PO_BOX_PATTERNS: RegExp[] = [
  /\bp\.?\s*o\.?\s*b(?:ox)?\s*(?:no\.?|number\.?|#)?\s*[A-Z0-9][A-Z0-9-]*/i,
  /\bp\.?\s*o\.?\s*b\.\s*(?:no\.?|number\.?|#)?\s*[A-Z0-9][A-Z0-9-]*/i,
  /\bpost\s*office\s*box\s*(?:no\.?|number\.?|#)?\s*[A-Z0-9][A-Z0-9-]*/i,
  /\bpo\s*drawer\s*(?:no\.?|number\.?|#)?\s*[A-Z0-9][A-Z0-9-]*/i,
];

export const PO_BOX_ERROR_MESSAGE = 'P.O. Box addresses are not allowed. Please enter a physical (residential) address.';

/** True when the control lives inside a mailing-address group, where P.O. Boxes are allowed. */
function isMailingAddressControl(control: AbstractControl): boolean {
  const group = control.parent;
  const container = group?.parent;
  if (!group || !container) return false;
  const groupKey = Object.keys((container as any).controls ?? {}).find((k) => (container as any).controls[k] === group);
  return typeof groupKey === 'string' && /mail/i.test(groupKey);
}

export function noPoBoxValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (typeof value !== 'string' || !value || isMailingAddressControl(control)) return null;
  return PO_BOX_PATTERNS.some((p) => p.test(value)) ? { poBox: true } : null;
}

/**
 * Blocks P.O. Box values on every address_line1 / address_line2 input bound to a reactive
 * control. Adds a `poBox` error to the control (so form.valid / Next / Verify / Submit gates
 * stay closed) and renders an inline error message under the input.
 */
@Directive({
  selector: `
    input[formControlName="address_line1"], input[formControlName="address_line2"],
    input[formControlName="addressLine1"], input[formControlName="addressLine2"],
    input[formControlName="physicalAddressLine1"], input[formControlName="physicalAddressLine2"],
    input[formControlName="physical_address_line1"], input[formControlName="physical_address_line2"]
  `,
  standalone: true,
  providers: [{ provide: NG_VALIDATORS, useExisting: NoPoBoxAddressDirective, multi: true }],
})
export class NoPoBoxAddressDirective implements Validator, OnInit, OnDestroy {
  private errorEl: HTMLElement | null = null;
  private reservedParent: HTMLElement | null = null;
  private previousMargin = '';
  private sub?: Subscription;
  private ngControl: NgControl | null = null;

  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
    private readonly injector: Injector,
    @Optional() private readonly messageService: MessageService | null,
  ) {}

  validate(control: AbstractControl): ValidationErrors | null {
    return noPoBoxValidator(control);
  }

  ngOnInit(): void {
    // Resolved lazily: injecting NgControl in the constructor is circular because
    // FormControlName itself depends on the NG_VALIDATORS this directive provides.
    this.ngControl = this.injector.get(NgControl, null, { self: true, optional: true });
    this.sub = this.ngControl?.statusChanges?.subscribe(() => this.render());
    this.el.nativeElement.addEventListener('blur', this.render);
    this.el.nativeElement.addEventListener('change', this.toast);
    this.render();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.el.nativeElement.removeEventListener('blur', this.render);
    this.el.nativeElement.removeEventListener('change', this.toast);
    this.removeError();
  }

  /** Error toast once the user commits a P.O. Box value (the single source of this toast app-wide). */
  private readonly toast = (): void => {
    const ctrl = this.ngControl?.control;
    if (!ctrl?.hasError('poBox')) return;
    const name = String(this.ngControl?.name ?? '');
    const label = /2$/.test(name) ? 'Address Line 2' : 'Address Line 1';
    this.messageService?.add({
      severity: 'error',
      summary: 'Error',
      life: 10000,
      detail: `The ${label} value that has been entered appears to be a P.O. Box. P.O. Box addresses are not allowed; please enter a physical (residential) address.`,
    });
  };

  private readonly render = (): void => {
    const ctrl = this.ngControl?.control;
    const show = !!ctrl?.hasError('poBox') && (ctrl.dirty || ctrl.touched || !!ctrl.value);
    if (!show) {
      this.removeError();
      return;
    }
    if (this.errorEl) return;
    const host = this.el.nativeElement;
    const small = this.renderer.createElement('small');
    this.renderer.addClass(small, 'text-red-500');
    this.renderer.setStyle(small, 'font-size', '12px');
    this.renderer.setStyle(small, 'display', 'block');
    this.renderer.appendChild(small, this.renderer.createText(PO_BOX_ERROR_MESSAGE));
    // Inside a position:relative wrapper (input + absolutely-centred icon, often a flex-row item)
    // the message must not take part in layout, or it stretches the wrapper / becomes a flex
    // sibling: pin it just under the input and reserve its height with a bottom margin instead.
    const parent = host.parentElement;
    if (parent && getComputedStyle(parent).position === 'relative') {
      this.renderer.setStyle(small, 'position', 'absolute');
      this.renderer.setStyle(small, 'left', host.offsetLeft + 'px');
      this.renderer.setStyle(small, 'top', host.offsetTop + host.offsetHeight + 4 + 'px');
      this.renderer.setStyle(small, 'width', host.offsetWidth + 'px');
      this.renderer.appendChild(parent, small);
      this.reservedParent = parent;
      this.previousMargin = parent.style.marginBottom;
      this.renderer.setStyle(parent, 'margin-bottom', small.offsetHeight + 6 + 'px');
    } else {
      this.renderer.insertBefore(host.parentNode, small, host.nextSibling);
    }
    this.errorEl = small;
  };

  private removeError(): void {
    if (this.errorEl?.parentNode) {
      this.renderer.removeChild(this.errorEl.parentNode, this.errorEl);
    }
    if (this.reservedParent) {
      this.renderer.setStyle(this.reservedParent, 'margin-bottom', this.previousMargin);
      this.reservedParent = null;
    }
    this.errorEl = null;
  }
}
